import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DomainBoard from '../components/DomainBoard.js';
import FindingsList, { type AuditFinding } from '../components/FindingsList.js';
import RunAuditControl from '../components/RunAuditControl.js';

const CISO_DOMAIN_LABELS: Record<string, string> = {
  secrets_credentials: 'Secrets & Credentials',
  authn_authz: 'AuthN/AuthZ',
  tenant_isolation: 'Tenant Isolation',
  injection: 'Injection',
  ssrf_egress: 'SSRF / Egress',
  crypto_at_rest: 'Crypto & Storage at Rest',
  transport_security: 'Transport Security',
  logging_pii: 'Logging & PII',
  fail_open_paths: 'Fail-Open Paths',
  dependency_supply_chain: 'Dependency & Supply Chain',
  compartmentalization_least_privilege: 'Compartmentalization & Least Privilege',
  input_validation: 'Input Validation',
  session_management: 'Session Management',
  audit_logging: 'Audit Logging',
  incident_readiness: 'Incident Readiness',
  data_retention_privacy: 'Data Retention & Privacy',
};

interface DomainVerdict {
  domain: string;
  status: string;
  note: string;
}

interface CodebaseAudit {
  id: string;
  repo: string;
  trigger: 'weekly' | 'on_demand';
  state: string;
  overall_status: 'green' | 'flag' | 'critical' | null;
  summary: string | null;
  domains: DomainVerdict[];
  session_url: string | null;
  started_at: string;
  completed_at: string | null;
}

interface PostureEntry {
  auditor: 'cto' | 'ciso';
  repo: string;
  latest_audit: CodebaseAudit | null;
  open_findings_by_severity: Record<string, number>;
  domains: DomainVerdict[];
}

function repoShortName(repo: string): string {
  return repo.split('/')[1] ?? repo;
}

function auditStatusBadgeClass(status: string): string {
  if (status === 'green') return 'badge-green';
  if (status === 'flag') return 'badge-yellow';
  if (status === 'critical') return 'badge-red';
  return 'badge-gray';
}

interface SecurityFinding {
  severity: 'info' | 'warn' | 'high' | 'critical';
  file: string;
  issue: string;
  standard?: string;
}

interface SecurityAudit {
  id: string;
  scope: string;
  posture: 'strong' | 'acceptable' | 'concerning' | 'critical';
  summary: string;
  findings: SecurityFinding[];
  reviewed_shas: string[];
  model: string | null;
  created_at: string;
}

interface OpenFinding {
  id: string;
  track_id: string;
  verdict: string;
  summary: string;
  findings: SecurityFinding[];
  created_at: string;
  title: string | null;
  pr_url: string | null;
}

function postureBadgeClass(posture: string): string {
  if (posture === 'strong') return 'badge-green';
  if (posture === 'acceptable') return 'badge-gray';
  if (posture === 'concerning') return 'badge-yellow';
  return 'badge-red';
}

function severityBadgeClass(severity: string): string {
  if (severity === 'critical' || severity === 'high') return 'badge-red';
  if (severity === 'warn') return 'badge-yellow';
  return 'badge-gray';
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(body?.error ?? `request failed: ${res.status}`);
  return body as T;
}

function AutomationToggle() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchJson<{ enabled: boolean }>('/api/settings/audit-automation')
      .then((r) => setEnabled(r.enabled))
      .catch(() => setEnabled(false));
  }, []);

  async function toggle() {
    if (enabled === null || busy) return;
    const prev = enabled;
    const next = !enabled;
    setEnabled(next);
    setBusy(true);
    try {
      const res = await fetch('/api/settings/audit-automation', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error(`request failed: ${res.status}`);
    } catch {
      setEnabled(prev);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="badge-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <label
        className="card-sub"
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: enabled === null || busy ? 'default' : 'pointer' }}
      >
        <input type="checkbox" checked={enabled ?? false} disabled={enabled === null || busy} onChange={toggle} />
        Automatic audits
      </label>
      {enabled === false && <span className="card-sub">on-demand only</span>}
    </div>
  );
}

export default function Security() {
  const [latest, setLatest] = useState<SecurityAudit | null>(null);
  const [history, setHistory] = useState<SecurityAudit[]>([]);
  const [findings, setFindings] = useState<OpenFinding[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [cisoPosture, setCisoPosture] = useState<PostureEntry[]>([]);
  const [codebaseFindings, setCodebaseFindings] = useState<AuditFinding[]>([]);
  const [codebaseHistory, setCodebaseHistory] = useState<CodebaseAudit[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetchJson<{ latest: SecurityAudit | null; history: SecurityAudit[] }>('/api/security/audits'),
      fetchJson<{ findings: OpenFinding[] }>('/api/security/findings'),
    ])
      .then(([audits, feedback]) => {
        setLatest(audits.latest);
        setHistory(audits.history);
        setFindings(feedback.findings);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    Promise.all([
      fetchJson<{ posture: PostureEntry[] }>('/api/audit-posture'),
      fetchJson<{ findings: AuditFinding[] }>('/api/audit-findings?auditor=ciso&status=open'),
      fetchJson<{ audits: CodebaseAudit[] }>('/api/audits?auditor=ciso'),
    ])
      .then(([p, f, a]) => {
        setCisoPosture(p.posture.filter((entry) => entry.auditor === 'ciso'));
        setCodebaseFindings(f.findings);
        setCodebaseHistory(a.audits.slice(0, 10));
      })
      .catch((e) => setError((e as Error).message));
  }, []);

  async function ackCodebaseFinding(id: string) {
    const prev = codebaseFindings;
    setBusyId(id);
    setCodebaseFindings((fs) => fs.map((f) => (f.id === id ? { ...f, status: 'acknowledged' } : f)));
    try {
      const res = await fetch(`/api/audit-findings/${id}/ack`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) throw new Error(`request failed: ${res.status}`);
    } catch (e) {
      setCodebaseFindings(prev);
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function resolveCodebaseFinding(id: string) {
    const prev = codebaseFindings;
    setBusyId(id);
    setCodebaseFindings((fs) => fs.map((f) => (f.id === id ? { ...f, status: 'resolved' } : f)));
    try {
      const res = await fetch(`/api/audit-findings/${id}/resolve`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) throw new Error(`request failed: ${res.status}`);
    } catch (e) {
      setCodebaseFindings(prev);
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <div className="empty">Loading…</div>;
  if (error) return <div className="errors">{error}</div>;

  return (
    <div>
      <div className="section">
        <AutomationToggle />
      </div>

      {cisoPosture.map((entry) => (
        <DomainBoard
          key={entry.repo}
          title={`${repoShortName(entry.repo)} — CISO domains`}
          domains={entry.domains}
          labels={CISO_DOMAIN_LABELS}
        />
      ))}

      <RunAuditControl auditor="ciso" />

      <div className="section">
        <h3>Security posture</h3>
        {latest ? (
          <>
            <div className="badge-row">
              <span className={`badge ${postureBadgeClass(latest.posture)}`}>{latest.posture}</span>
              <span className="card-sub">audited {new Date(latest.created_at).toLocaleString()}</span>
            </div>
            <div className="one-line">{latest.summary}</div>
          </>
        ) : (
          <div className="empty">No daily audit has run yet.</div>
        )}
      </div>

      <div className="section">
        <h3>Open CISO findings</h3>
        {findings.length === 0 && <div className="empty">No open findings.</div>}
        {findings.map((f) => (
          <div key={f.id} className="card" style={{ display: 'block' }}>
            <div className="badge-row">
              <span className={`badge ${f.verdict === 'block' ? 'badge-red' : 'badge-yellow'}`}>{f.verdict}</span>
              {f.title && f.track_id && (
                <Link className="card-sub" to={`/tracks/${f.track_id}`}>{f.title}</Link>
              )}
              {f.pr_url && (
                <a className="card-sub" href={f.pr_url} target="_blank" rel="noreferrer">PR</a>
              )}
            </div>
            <div className="one-line">{f.summary}</div>
            {f.findings.length > 0 && (
              <ul>
                {f.findings.map((item, i) => (
                  <li key={i}>
                    <span className={`badge ${severityBadgeClass(item.severity)}`}>{item.severity}</span>{' '}
                    {item.file} — {item.issue}
                    {item.standard ? ` (${item.standard})` : ''}
                  </li>
                ))}
              </ul>
            )}
            <div className="card-sub">acknowledge via the ack_review MCP tool — no HTTP ack endpoint exists yet</div>
          </div>
        ))}
      </div>

      <div className="section">
        <h3>Audit history</h3>
        {history.length === 0 && <div className="empty">No audits recorded yet.</div>}
        {history.map((a) => (
          <div key={a.id} className="card" style={{ display: 'block' }}>
            <div className="badge-row">
              <span className={`badge ${postureBadgeClass(a.posture)}`}>{a.posture}</span>
              <span className="card-sub">{new Date(a.created_at).toLocaleString()}</span>
              <span className="card-sub">{a.reviewed_shas.length} PRs reviewed</span>
            </div>
            <div className="one-line">{a.summary}</div>
          </div>
        ))}
      </div>

      <div className="section">
        <h3>Codebase audit findings</h3>
        <div className="card-sub" style={{ marginBottom: 8 }}>source: full-codebase CISO audit, distinct from the PR-review findings above</div>
        <FindingsList
          findings={codebaseFindings}
          labels={CISO_DOMAIN_LABELS}
          onAck={ackCodebaseFinding}
          onResolve={resolveCodebaseFinding}
          busyId={busyId}
        />
      </div>

      <div className="section">
        <h3>Codebase audit history (CISO)</h3>
        {codebaseHistory.length === 0 && <div className="empty">No codebase audits recorded yet.</div>}
        {codebaseHistory.map((a) => (
          <div key={a.id} className="card" style={{ display: 'block' }}>
            <div className="badge-row" style={{ flexWrap: 'wrap' }}>
              <span className={`badge ${auditStatusBadgeClass(a.overall_status ?? 'gray')}`}>{a.overall_status ?? a.state}</span>
              <span className="card-sub">{repoShortName(a.repo)}</span>
              <span className="card-sub">{a.trigger}</span>
              <span className="card-sub">{new Date(a.started_at).toLocaleString()}</span>
              {a.session_url && (
                <a className="card-sub" href={a.session_url} target="_blank" rel="noreferrer">session</a>
              )}
            </div>
            {a.summary && <div className="one-line">{a.summary}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
